"use client";

// Consolidated Board Packet (part-2 notes §1.2): every policy currently before
// the board, with each statement's full journey — original text, current text,
// who changed it — plus route-gate status. Print-friendly so staff can hand the
// board a single document per meeting cycle (browser print → save as PDF).

import { useEffect, useState } from "react";
import { getBoardPacket } from "@/lib/policies/api";
import { StatusChip } from "../StatusChip";
import { strip } from "../theme";

export function BoardPacketView() {
    const [policies, setPolicies] = useState<any[] | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        getBoardPacket().then(setPolicies).catch((e) => setError(e.message));
    }, []);

    if (error) return <div className="p-6 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>;
    if (policies === null) {
        return (
            <div className="py-16 flex justify-center">
                <div className="w-8 h-8 border-2 border-[#007a8a]/20 border-t-[#007a8a] rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div id="board-packet">
            <style>{`
                @media print {
                    body * { visibility: hidden; }
                    #board-packet, #board-packet * { visibility: visible; }
                    #board-packet { position: absolute; left: 0; top: 0; width: 100%; }
                    #board-packet .no-print { display: none !important; }
                    #board-packet .packet-policy { break-inside: avoid-page; page-break-inside: avoid; }
                }
            `}</style>

            <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                    <h2 className="text-lg font-bold text-[#15302f]">Board Packet — policies before the board</h2>
                    <p className="text-[12.5px] text-[#5f7173] mt-1 max-w-2xl">
                        Every policy in &ldquo;Proposed to Board&rdquo;, consolidated for one review cycle: the full journey
                        per statement (original → current text, who touched it) so the board never votes blind.
                    </p>
                </div>
                <button
                    onClick={() => window.print()}
                    className="no-print flex-none border-none bg-[#004d57] text-white text-[12.5px] font-bold rounded-lg px-4 py-2"
                >
                    🖨 Print / save as PDF
                </button>
            </div>

            {policies.length === 0 && (
                <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-sm text-[#88a39b]">
                    No policies are currently before the board.
                </div>
            )}

            <div className="space-y-5">
                {policies.map((p) => (
                    <div key={p.id} className="packet-policy bg-white border border-gray-200 rounded-xl overflow-hidden">
                        <div className="px-5 py-4 border-b border-[#eef1ef] flex items-start justify-between gap-3 flex-wrap">
                            <div>
                                <div className="font-bold text-[15px] text-[#004d57]">{p.id} · {p.name}</div>
                                <div className="text-xs text-[#5f7173] mt-1">
                                    Owner: {p.proposed_owner_group || "—"}
                                    {p.assignment?.steward_name && <> · Steward: {p.assignment.steward_name}</>}
                                    {p.board_involvement && <> · Board involvement: <b className="capitalize">{p.board_involvement}</b></>}
                                </div>
                            </div>
                            <StatusChip state={p.lifecycle_state} />
                        </div>

                        {p.gates.length > 0 && (
                            <div className="px-5 py-2.5 border-b border-[#eef1ef] flex gap-2 flex-wrap">
                                {p.gates.map((g: any) => (
                                    <span key={g.gate}
                                        className={`text-[10.5px] font-bold rounded px-2 py-1 ${g.approved ? "bg-[#dceee7] text-[#2c7a63]" : "bg-[#fbecd6] text-[#9a6a16]"}`}>
                                        {g.approved ? "✓" : "○"} {g.label}{g.approved && g.approved_by ? ` — ${g.approved_by}` : ""}
                                    </span>
                                ))}
                            </div>
                        )}

                        <div className="divide-y divide-[#f2f4f3]">
                            {p.statements.map((s: any) => (
                                <div key={s.sid} className="px-5 py-3.5">
                                    <div className="flex items-center gap-2 mb-1.5">
                                        <span className="text-[11px] font-mono font-bold text-[#88a39b]">{s.sid}</span>
                                        {s.changed ? (
                                            <span className="text-[9.5px] font-bold bg-[#e6e9f2] text-[#4d5aa0] rounded px-1.5 py-0.5">
                                                CHANGED · v{s.version}{s.changed_by.length > 0 && ` · by ${Array.from(new Set(s.changed_by)).join(", ")}`}
                                            </span>
                                        ) : (
                                            <span className="text-[9.5px] font-bold bg-[#ece9e2] text-[#7a786f] rounded px-1.5 py-0.5">UNCHANGED</span>
                                        )}
                                    </div>
                                    {s.changed && (
                                        <p className="text-[12.5px] leading-relaxed text-[#9a4b4b] line-through opacity-80 mb-1">{strip(s.original_text)}</p>
                                    )}
                                    <p className="text-[13px] leading-relaxed text-[#2c3e40]">{strip(s.current_text)}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
